import React, {
  useMemo, useEffect, useState, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import english from '../../assets/lang/english';
import { RootState } from '../../redux/rootState';
import DataTable from '../../Common/DataTable';
import ComponentDeleteModal from '../../Common/DeleteModal/ComponentDeleteModal';
import { setLoader } from '../../redux/loaderSlice';
import ComponentPageHeader from '../../Common/PageHeader/ComponentPageHeader';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import { ExpenseReportsData } from './utils/types.d';
// import { tempDataArray } from './utils/constants';
import { deleteExpenseReportStart, getExpenseReportData } from './redux/expenseReportSlice';
import EndPoints from '../../Routes/EndPoints';
import { convertDateFormat } from '../../utils/CommonFunctions';
import {
  resetPagination, setPageNumber, setPageSize, setSearchParam,
  setSortColumn,
  setSortDirection,
} from '../taiReportingSystem/redux/sliceReportWorkspace';
import { setCheckedIds } from '../jobOrder/redux/sliceJobOrder';
import { clearExpenseReportPdfData, getExpenseReportPdf } from '../ComponentNavBar/redux/dashboardSlice';
import ScreenExpenseReportPdf from '../ComponentNavBar/ScreenExpenseReportPdf';

export default function ScreenExpenseReports() {
  const printRef = useRef(null);
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  // const { jobOrder } = useParams();
  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState('');
  const [tableData, setTableData] = useState<ExpenseReportsData[]>([]);

  const { isLoading, expenseReportData } = useSelector((state: RootState) => state.expenseReportReducer);
  const { checkedIds } = useSelector((state: RootState) => state.jobOrderReducer);
  const {
    expenseReportPdfData, isMorningRepPdfLoading,
  } = useSelector((state:RootState) => state.dashboardReducer);

  const handleData = () => {
    const params = {
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
    };
    dispatch(getExpenseReportData(params));
  };

  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection]);

  useEffect(() => () => {
    dispatch(setCheckedIds([]));
  }, []);

  // useEffect(() => {
  //   if (jobOrder) {
  //     dispatch(setSearchParam(jobOrder));
  //   } else dispatch(setSearchParam(''));
  // }, [jobOrder]);

  const handleDelete = (id: string) => {
    const ids = id.split('/');
    setShowDeleteModal(true);
    setSelectedExpenseId(ids[1]);
  };
  const handelDeleteModal = () => {
    const paramData = {
      id: selectedExpenseId,
      page: pageNumber,
      rowsPerPage: pageSize,
      searchQuery: searchParam,
      sortCol: sortColumn,
      sortDir: sortDirection,
    };
    dispatch(deleteExpenseReportStart(paramData));
    setShowDeleteModal(false);
  };
  const columns = useMemo(
    () => [
      { key: 'sn', label: '' },
      { key: 'action', label: 'Action' },
      { key: 'JobOrder', label: 'Job Order #' },
      // { key: 'ExpDate', label: 'Date' },
      { key: 'ExpDate', label: 'Check Date' },
      { key: 'AnalystName', label: 'Analyst' },
      { key: 'CheckNo', label: 'Total $' },
    ],
    [],
  );
  const data: ExpenseReportsData[] = useMemo(() => expenseReportData?.Data?.map((item, index) => {
    const transformedDataItem = {
      sn: index,
      action: 'edit&delete',
      JobOrder: item?.JobOrder,
      ExpDate: convertDateFormat(item?.ExpDate),
      AnalystName: item?.AnalystName,
      CheckNo: `${item?.CheckNo?.trim()?.length > 0 ? item?.CheckNo : 0} $`,
      // ExpDate: convertDateFormat(item?.ExpDate),
      id: `${item.JobOrder}/${item?.Id}`,
      isChecked: checkedIds?.includes(`${item.JobOrder}/${item?.Id}`),
    };
    return transformedDataItem;
  }), [expenseReportData, checkedIds]);

  const handleCheckBoxWithKey = (id: string) => {
    const jobIds: string[] = [...checkedIds];
    const updatedData = data?.map((element: ExpenseReportsData) => {
      if (element?.id === id) {
        if (!element.isChecked) {
          jobIds.push(element.id);
        } else {
          const ind = jobIds.findIndex((ele) => ele === id);
          jobIds.splice(ind, 1);
        }
        return {
          ...element,
          isChecked: !element?.isChecked,
        };
      }
      return element;
    });

    setTableData(updatedData);
    dispatch(setCheckedIds(jobIds));
  };

  useEffect(() => {
    if (expenseReportData) {
      setTableData(data);
    }
  }, [expenseReportData, data]);

  useEffect(() => {
    dispatch(setLoader(isLoading));
  }, [isLoading]);

  const handleEdit = (id:string) => {
    Navigate(`/${EndPoints.EXPENSE_REPORTS}/${id}`);
  };

  const excelData = useMemo(() => expenseReportData?.Data?.map((item) => {
    const transformedDataItem = {
      JobOrder: item?.JobOrder,
      CheckDate: convertDateFormat(item?.ExpDate),
      Analyst: item?.AnalystName,
      Total$: item?.CheckNo,
      // Date: convertDateFormat(item?.ExpDate),
    };
    return transformedDataItem;
  }), [expenseReportData]);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'Expense_report',
    );
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = '/expense-reports/';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(
        last?.includes(finderString)
          || (sLast?.includes(finderString) && last === '/expense-reports')
      )) {
        dispatch(resetPagination());
      }
    }
  }, [historyStack]);

  const handleApiCall = () => {
    const jobIds = checkedIds?.map((item) => item?.split('/')[0]);
    dispatch(getExpenseReportPdf({ JobOrders: jobIds }));
  };

  useEffect(() => {
    dispatch(setLoader(isMorningRepPdfLoading));
  }, [isMorningRepPdfLoading]);

  const handlePrint = useReactToPrint({
    content: () => printRef?.current,
    removeAfterPrint: true,
    copyStyles: true,
    onAfterPrint: () => {
      dispatch(setCheckedIds([]));
      dispatch(clearExpenseReportPdfData());
    },
    documentTitle: 'Expense Report',
  });

  useEffect(() => {
    if (Object.keys(expenseReportPdfData).length > 0) {
      handlePrint();
    }
  }, [expenseReportPdfData]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12}>
          <ComponentPageHeader subHeading={String(english.expenseReports)} downloadExcel={downloadExcel} />
        </Grid>
        <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
          <DataTable<ExpenseReportsData>
            data={tableData}
            columns={columns}
            totalRecords={expenseReportData?.TotalRecords}
            page={pageNumber}
            setPage={(e) => { dispatch(setPageNumber(e)); }}
            rowsPerPage={pageSize}
            setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
            searchQuery={searchParam}
            setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
            sortCol={sortColumn}
            setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
            sortDir={sortDirection}
            setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
            handleData={handleData}
            handleDelete={handleDelete}
            handleEdit={handleEdit}
            handleDataFromComponent
            checkBoxWithKey={['sn']}
            handleCheckBoxWithKey={handleCheckBoxWithKey}
            buttonText="Generate Report"
            handleButtonClick={handleApiCall}
            textButtonDisabled={checkedIds?.length === 0}
          />
        </Grid>
      </Grid>
      <ComponentDeleteModal
        show={showDeleteModal}
        setOpenFrom={() => setShowDeleteModal(false)}
        handleDelete={handelDeleteModal}
      />
      <ScreenExpenseReportPdf printRef={printRef} />
    </>
  );
}
